'use client'

import { BottomSheet } from '@fukku/dialog/BottomSheet/BottomSheet'
import { Content } from '@fukku/dialog/Content/Content'
import type { DialogAPI } from '@fukku/dialog/Dialog'
import { Header } from '@fukku/dialog/Header/Header'
import {
	SideSheet,
	type SideSheetProps,
} from '@fukku/dialog/SideSheet/SideSheet'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import dynamic from 'next/dynamic'
import type { ProductFamily } from 'product/types/Product'
import type { ForwardRefExoticComponent, RefAttributes } from 'react'
import { useResponsiveBehavior } from 'responsive/hooks/useResponsiveBehavior/useResponsiveBehavior'
import { useSizeGuideDialogRef } from 'size-guide/contexts/SizeGuideDialogRefProvider/SizeGuideDialogRefProvider'
import styles from './SizeGuideDialog.module.css'

const DynamicSizeGuideClient = dynamic(
	() =>
		import('size-guide/components/SizeGuide/client/SizeGuideClient').then(
			(component) => component.SizeGuideClient
		),
	{
		loading: () => (
			<div className={styles.loading} data-testid='size-guide-loading' />
		),
	}
)

interface SizeGuideDialogProps {
	readonly genderId: string
	readonly families: ProductFamily[]
	readonly onSizeGuideClose: () => void
}

export function SizeGuideDialog({
	genderId,
	families,
	onSizeGuideClose,
}: SizeGuideDialogProps): React.ReactNode {
	const { t } = useLabels()

	const { sizeGuideDialogRef } = useSizeGuideDialogRef()

	const ResponsiveDialog = useResponsiveBehavior<
		ForwardRefExoticComponent<SideSheetProps & RefAttributes<DialogAPI>>
	>({
		default: SideSheet,
		small: BottomSheet,
	})

	const familyIds = families.map((family) => family.id).join(',')

	return (
		<ResponsiveDialog
			title={t('sizeGuide.link')}
			onClose={onSizeGuideClose}
			ref={sizeGuideDialogRef}
			extraWidth
		>
			<Header title={t('sizeGuide.link')} />
			<Content>
				<DynamicSizeGuideClient
					searchParams={{ line: genderId, familyIds }}
					isModal
				/>
			</Content>
		</ResponsiveDialog>
	)
}
